// client/src/components/AnnouncementForm.js
//
// Coach-side create flow. Sits at /announcements/new, reached from the
// megaphone garnish on AnnouncementsHome. Composes the Session A form
// primitives against the POST /announcements contract locked in §3f.
//
// Flow: submit → toast(success) → navigate("/") → the announcement feed
// refetches (useFetch in AnnouncementsHome re-runs on mount) → new
// announcement appears at the top (or wherever pinned/importance sort
// puts it). Refetch over optimistic prepend per §3c — the server owns
// id, is_expired, and scope-name lookups, so the source of truth is
// the refetched feed.
//
// Layout follows the app-level route convention (per §3d): PfShell with
// showChrome=false, since AppChromeLayout handles the app-level top bar
// at viewport level. PageTitle carries the mischief garnish so the form
// still reads like MCYH and not a generic CRUD screen (§3b).
//
// More options (importance, expires_at) collapse under a <details>
// toggle. ~95% of announcements are Normal + no expiry — surfacing
// four radio buttons and a datetime input by default pushes real
// fields (title, body, scope, pinned) below the fold.

import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useOrg } from '../lib/OrgProvider';
import { useToast } from '../lib/ToastProvider';
import { PfShell } from './shell/PfShell';
import { PageTitle } from './shell/PageTitle';
import { Highlight } from './mischief/Highlight';
import { Scrawl } from './mischief/Scrawl';
import { FormField } from './forms/FormField';
import { TextInput } from './forms/TextInput';
import { Textarea } from './forms/Textarea';
import { Select } from './forms/Select';
import { Checkbox } from './forms/Checkbox';
import { DateTimePicker } from './forms/DateTimePicker';
import { SubmitButton } from './forms/SubmitButton';
import { CascadingScopePicker } from './forms/CascadingScopePicker';

// API base — same pattern useFetch uses. Kept as a const here rather
// than reaching into useFetch's config because useFetch is GET-only;
// we're calling POST directly with fetch().
// TODO: env-ify alongside the other hardcoded URLs (open item from
// the migration handoff).
const API_BASE = 'https://server-dev.bightyouthsports.com/server/ysa-api';

export default function AnnouncementForm() {
  const { activeOrg } = useOrg();
  const toast = useToast();
  const navigate = useNavigate();

  // Form state. Kept flat rather than one object because the fields are
  // independent — no nested update logic worth the ceremony.
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [scope, setScope] = useState({ scope: 'org' });
  const [pinned, setPinned] = useState(false);
  const [importance, setImportance] = useState('Normal');
  const [expiresAt, setExpiresAt] = useState('');

  // Submit lifecycle. Client-side error is a top-level string shown at
  // the bottom of the form (form-level error). Field-level errors would
  // need per-field state; deferred until we have a validation case that
  // benefits from field-scoped messaging.
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);

  // Belt-and-suspenders visibility gate. AnnouncementsHome hides the
  // megaphone from Guardians, but a Guardian who somehow lands on this
  // URL directly (bookmark, share link, etc.) should get a soft bounce
  // rather than a form that will 403 on submit.
  const canPost =
    activeOrg?.roles?.includes('Coach') ||
    activeOrg?.roles?.includes('Admin');

  if (!canPost) {
    return (
      <PfShell showChrome={false}>
        <div className="px-5 pt-10 pb-8 text-center">
          <p className="font-serif text-[28px] text-ink leading-tight">
            Not for you (yet).
          </p>
          <Scrawl className="mt-2 inline-block" color="inkSub">
            only coaches and admins can post
          </Scrawl>
          <div className="mt-6">
            <Link to="/" className="text-sm text-marker underline">
              Back to announcements
            </Link>
          </div>
        </div>
      </PfShell>
    );
  }

  // Client-side pre-flight. The server does its own full validation
  // (see §3f); this is just early-catch for the common empties.
  function validate() {
    if (!title.trim()) return 'Title is required.';
    if (title.length > 80) return 'Title must be 80 characters or fewer.';
    if (!body.trim()) return 'Body is required.';
    if (scope.scope === 'age_group' && !scope.age_group_id) {
      return 'Pick an age group.';
    }
    if (scope.scope === 'team' && !scope.team_id) {
      return 'Pick a team.';
    }
    return null;
  }

  async function handleSubmit(e) {
    e.preventDefault();

    const err = validate();
    if (err) {
      setFormError(err);
      return;
    }
    setFormError(null);
    setSubmitting(true);

    // Assemble body per §3f contract. Only send FK ids that are set;
    // undefined keys get dropped by JSON.stringify, matching the
    // optional-field shape the server expects.
    const payload = {
      title: title.trim(),
      body: body.trim(),
      scope: scope.scope,
      age_group_id: scope.age_group_id || undefined,
      team_id: scope.team_id || undefined,
      importance,
      pinned,
      expires_at: expiresAt || undefined,
    };

    try {
      const res = await fetch(`${API_BASE}/announcements`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const responseBody = await res.json().catch(() => null);

      if (!res.ok || !responseBody?.success) {
        // Show server error verbatim when we have one — coaches will
        // hit real cases (empty body, bad scope, etc.) and the server
        // message is more specific than a generic "something broke."
        const message =
          responseBody?.error ||
          `Couldn't post announcement (${res.status})`;
        setFormError(message);
        toast.error(message);
        setSubmitting(false);
        return;
      }

      // Success. Toast, then navigate — AnnouncementsHome's useFetch
      // re-runs on mount and picks up the new row.
      toast.success('Announcement posted');
      navigate('/');
    } catch (networkErr) {
      const message = 'Network error — check your connection and try again.';
      setFormError(message);
      toast.error(message);
      setSubmitting(false);
    }
  }

  return (
    <PfShell showChrome={false}>
      <form onSubmit={handleSubmit} className="px-5 pt-6 pb-8" noValidate>
        <PageTitle eyebrow="ANNOUNCEMENTS">
          New <Highlight color="yellow">announcement</Highlight>
        </PageTitle>

        <div className="mt-6 space-y-5">
          <FormField
            label="Title"
            helperText="Keep it short — people scan headlines"
          >
            <TextInput
              value={title}
              onChange={setTitle}
              placeholder="Rink 2 closed Saturday"
              maxLength={80}
              autoFocus
            />
          </FormField>

          <FormField label="Body">
            <Textarea
              value={body}
              onChange={setBody}
              rows={6}
              placeholder="What's happening, when, and where."
            />
          </FormField>

          <CascadingScopePicker value={scope} onChange={setScope} />

          <Checkbox
            label="Pin to top for 24 hours"
            checked={pinned}
            onChange={setPinned}
          />

          {/* More options — collapsed by default. Coaches who never
              touch this get a cleaner form; the coach who needs Urgent
              for a rink closure can find it. */}
          <details className="border-t border-rule pt-4">
            <summary className="cursor-pointer text-sm font-semibold text-ink select-none">
              More options
            </summary>

            <div className="mt-4 space-y-5">
              <FormField label="Importance">
                <Select value={importance} onChange={setImportance}>
                  <option value="Low">Low</option>
                  <option value="Normal">Normal</option>
                  <option value="High">High</option>
                  <option value="Urgent">Urgent</option>
                </Select>
              </FormField>

              <FormField
                label="Expires at"
                helperText={
                  pinned && !expiresAt
                    ? 'Leaving blank means pinned posts auto-expire in 24h'
                    : 'Optional — leave blank for no expiration'
                }
              >
                <DateTimePicker
                  value={expiresAt}
                  onChange={setExpiresAt}
                />
              </FormField>
            </div>
          </details>

          {formError && (
            <p className="text-sm text-red">{formError}</p>
          )}

          <div className="pt-2 space-y-3">
            <SubmitButton loading={submitting}>
              Post announcement
            </SubmitButton>

            {/* Cancel is a text link, not a button — one primary
                action per form, hierarchy stays unambiguous (§3b). */}
            <div className="text-center">
              <Link
                to="/"
                className="text-sm text-inkSub hover:text-ink underline"
              >
                Cancel
              </Link>
            </div>
          </div>
        </div>
      </form>
    </PfShell>
  );
}